/**
 * ModalStateManager manages the global state of modals in the application.
 * It works with NavigationManager to handle back button behavior for modals.
 */

import NavigationManager from './NavigationManager';

export interface ModalState {
  reportForm: boolean;
  emojiExplanation: boolean;
  inspectionSource: boolean;
  badge: boolean;
}

type ModalStateListener = (state: ModalState) => void;

class ModalStateManager {
  private static instance: ModalStateManager;
  private state: ModalState = {
    reportForm: false,
    emojiExplanation: false,
    inspectionSource: false,
    badge: false
  };
  private listeners: Set<ModalStateListener> = new Set();
  private navigationManager: NavigationManager;

  private constructor() {
    this.navigationManager = NavigationManager.getInstance();
  }

  public static getInstance(): ModalStateManager {
    if (!ModalStateManager.instance) {
      ModalStateManager.instance = new ModalStateManager();
    }
    return ModalStateManager.instance;
  }

  /**
   * Open a modal and register it with NavigationManager
   */
  public openModal(modalName: keyof ModalState): void {
    if (this.state[modalName]) return; // Already open

    this.state = { ...this.state, [modalName]: true };
    this.notifyListeners();

    // Register with NavigationManager for back button handling
    this.navigationManager.registerOverlay(
      'modal',
      modalName,
      () => this.closeModal(modalName)
    );
  }

  /**
   * Close a modal and unregister from NavigationManager
   */
  public closeModal(modalName: keyof ModalState): void {
    if (!this.state[modalName]) return; // Already closed

    this.state = { ...this.state, [modalName]: false };
    this.notifyListeners();

    // Unregister from NavigationManager
    this.navigationManager.unregisterOverlay(modalName);
  }

  /**
   * Close all modals
   */
  public closeAllModals(): void {
    Object.keys(this.state).forEach(modalName => {
      if (this.state[modalName as keyof ModalState]) {
        this.closeModal(modalName as keyof ModalState);
      }
    });
  }

  /**
   * Check if a specific modal is open
   */
  public isModalOpen(modalName: keyof ModalState): boolean {
    return this.state[modalName];
  }

  /**
   * Check if any modal is open
   */
  public hasOpenModals(): boolean {
    return Object.values(this.state).some(isOpen => isOpen);
  }

  /**
   * Get the current state of all modals
   */
  public getState(): ModalState {
    return { ...this.state };
  }

  /**
   * Subscribe to modal state changes
   */
  public subscribe(listener: ModalStateListener): () => void {
    this.listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      listener(this.getState());
    });
  }
}

export default ModalStateManager;